import { pluralizeWord } from '@payments-checkout/shared/helpers/pluralize';
import { PlanInterval } from '../../types/paymentLink.types';

interface FrequencyLabel {
  i18nKey: string;
  fallback: string;
  count: number;
}

// Map known combinations to legacy keys for backward compatibility
const getLegacyKey = (interval: PlanInterval, intervalCount: number): string | null => {
  // Legacy keys only apply to month and year intervals
  if (interval === 'month') {
    switch (intervalCount) {
      case 1:
        return '30Days';
      case 3:
        return '90Days';
      case 6:
        return '180Days';
      default:
        return null;
    }
  }
  if (interval === 'year' && intervalCount === 1) {
    return '360Days';
  }
  return null;
};

export const getFrequencyLabel = (
  interval: PlanInterval,
  intervalCount: number
): FrequencyLabel => {
  // Check for legacy mapping first (for backward compatibility)
  const legacyKey = getLegacyKey(interval, intervalCount);
  if (legacyKey) {
    return {
      i18nKey: `recurrence.interval.${legacyKey}`,
      fallback: getFallbackLabel(interval, intervalCount),
      count: intervalCount,
    };
  }

  // Generic mapping for new combinations
  // Singular forms (count = 1)
  if (intervalCount === 1) {
    switch (interval) {
      case 'day':
        return { i18nKey: 'frequency.day.singular', fallback: 'Todo dia', count: 1 };
      case 'week':
        return { i18nKey: 'frequency.week.singular', fallback: 'Toda semana', count: 1 };
      case 'month':
        return { i18nKey: 'frequency.month.singular', fallback: 'Todo mês', count: 1 };
      case 'year':
        return { i18nKey: 'frequency.year.singular', fallback: 'Todo ano', count: 1 };
    }
  }

  // Plural forms (count > 1)
  switch (interval) {
    case 'day':
      return {
        i18nKey: 'frequency.day.plural',
        fallback: `A cada ${intervalCount} dias`,
        count: intervalCount,
      };
    case 'week':
      return {
        i18nKey: 'frequency.week.plural',
        fallback: `A cada ${intervalCount} semanas`,
        count: intervalCount,
      };
    case 'month':
      return {
        i18nKey: 'frequency.month.plural',
        fallback: `A cada ${intervalCount} meses`,
        count: intervalCount,
      };
    case 'year':
      return {
        i18nKey: 'frequency.year.plural',
        fallback: `A cada ${intervalCount} anos`,
        count: intervalCount,
      };
    default:
      // Safe fallback for unexpected combinations
      return {
        i18nKey: 'frequency.fallback',
        fallback: `A cada ${intervalCount} ${interval}s`,
        count: intervalCount,
      };
  }
};

const getFallbackLabel = (interval: PlanInterval, intervalCount: number): string => {
  if (interval === 'month') {
    if (intervalCount === 1) return 'Mensal (a cada 30 dias)';
    if (intervalCount === 3) return 'Trimestral (a cada 90 dias)';
    if (intervalCount === 6) return 'Semestral (a cada 180 dias)';
  }
  if (interval === 'year' && intervalCount === 1) {
    return 'Anual (a cada 365 dias)';
  }

  const pluralMap: Record<PlanInterval, { singular: string; plural: string }> = {
    day: { singular: 'dia', plural: 'dias' },
    week: { singular: 'semana', plural: 'semanas' },
    month: { singular: 'mês', plural: 'meses' },
    year: { singular: 'ano', plural: 'anos' },
  };

  const intervalLabel = pluralMap[interval];
  return `A cada ${pluralizeWord(intervalLabel.singular, intervalCount, 'hideNumber', intervalLabel.plural)}`;
};

export const getFrequencyTitle = (
  interval: PlanInterval,
  intervalCount: number
): FrequencyLabel => {
  // Check for legacy mapping first (for backward compatibility)
  const legacyKey = getLegacyKey(interval, intervalCount);
  if (legacyKey) {
    return {
      i18nKey: `recurrenceStatusCard.everyBilling.${legacyKey}.title`,
      fallback: getFallbackTitle(interval, intervalCount),
      count: intervalCount,
    };
  }

  // Generic mapping for new combinations
  // Singular forms (count = 1)
  if (intervalCount === 1) {
    switch (interval) {
      case 'day':
        return {
          i18nKey: 'frequency.title.day.singular',
          fallback: 'As cobranças seguintes vão ser feitas diariamente.',
          count: 1,
        };
      case 'week':
        return {
          i18nKey: 'frequency.title.week.singular',
          fallback: 'As cobranças seguintes vão ser feitas semanalmente.',
          count: 1,
        };
      case 'month':
        return {
          i18nKey: 'frequency.title.month.singular',
          fallback: 'As cobranças seguintes vão ser feitas mensalmente.',
          count: 1,
        };
      case 'year':
        return {
          i18nKey: 'frequency.title.year.singular',
          fallback: 'As cobranças seguintes vão ser feitas anualmente.',
          count: 1,
        };
    }
  }

  // Plural forms (count > 1)
  switch (interval) {
    case 'day':
      return {
        i18nKey: 'frequency.title.day.plural',
        fallback: `As cobranças seguintes vão ser feitas a cada ${intervalCount} dias.`,
        count: intervalCount,
      };
    case 'week':
      return {
        i18nKey: 'frequency.title.week.plural',
        fallback: `As cobranças seguintes vão ser feitas a cada ${intervalCount} semanas.`,
        count: intervalCount,
      };
    case 'month':
      return {
        i18nKey: 'frequency.title.month.plural',
        fallback: `As cobranças seguintes vão ser feitas a cada ${intervalCount} meses.`,
        count: intervalCount,
      };
    case 'year':
      return {
        i18nKey: 'frequency.title.year.plural',
        fallback: `As cobranças seguintes vão ser feitas a cada ${intervalCount} anos.`,
        count: intervalCount,
      };
    default:
      return {
        i18nKey: 'frequency.title.fallback',
        fallback: `As cobranças seguintes vão ser feitas a cada ${intervalCount} ${interval}s.`,
        count: intervalCount,
      };
  }
};

const getFallbackTitle = (interval: PlanInterval, intervalCount: number): string => {
  if (interval === 'month') {
    if (intervalCount === 1) return 'As cobranças seguintes vão ser feitas a cada 30 dias.';
    if (intervalCount === 3) return 'As cobranças seguintes vão ser feitas a cada 90 dias.';
    if (intervalCount === 6) return 'As cobranças seguintes vão ser feitas a cada 180 dias.';
  }
  if (interval === 'year' && intervalCount === 1) {
    return 'As cobranças seguintes vão ser feitas a cada 365 dias.';
  }

  const pluralMap: Record<PlanInterval, { singular: string; plural: string }> = {
    day: { singular: 'dia', plural: 'dias' },
    week: { singular: 'semana', plural: 'semanas' },
    month: { singular: 'mês', plural: 'meses' },
    year: { singular: 'ano', plural: 'anos' },
  };

  const intervalLabel = pluralMap[interval];
  const formattedInterval = pluralizeWord(intervalLabel.singular, intervalCount, 'hideNumber', intervalLabel.plural);
  return `As cobranças seguintes vão ser feitas a cada ${intervalCount} ${formattedInterval}.`;
};
